
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Pencil, Copy, Trash2, MoreHorizontal } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ChatActionsProps } from "../types";

const ChatActions = ({ 
  sessionId, 
  currentTitle, 
  onRename, 
  onDuplicate, 
  onDelete 
}: ChatActionsProps) => {
  const { user } = useAuth();
  const [isRenameOpen, setIsRenameOpen] = useState(false);
  const [newTitle, setNewTitle] = useState(currentTitle);
  const [isLoading, setIsLoading] = useState(false);

  const handleRename = async () => {
    if (!user || !newTitle.trim()) return;
    
    setIsLoading(true);
    try {
      const { error } = await supabase
        .from('product_chat_sessions')
        .update({ title: newTitle.trim() })
        .eq('id', sessionId)
        .eq('user_id', user.id);

      if (error) throw error;

      onRename(sessionId, newTitle.trim());
      setIsRenameOpen(false);
      toast.success('Chat renamed successfully');
    } catch (error) {
      console.error('Error renaming chat:', error);
      toast.error('Failed to rename chat');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDuplicate = async () => {
    if (!user) return;
    
    setIsLoading(true);
    try {
      // Get the original session data including template info
      const { data: originalSession, error: sessionError } = await supabase
        .from('product_chat_sessions')
        .select('title, template_id')
        .eq('id', sessionId)
        .single();

      if (sessionError) throw sessionError;

      // Create a new session with a clear "Copy" suffix
      const duplicatedTitle = `${originalSession.title} (Copy)`;
      const { data: newSession, error: createError } = await supabase
        .from('product_chat_sessions')
        .insert({
          user_id: user.id,
          title: duplicatedTitle,
          template_id: originalSession.template_id
        })
        .select()
        .single();

      if (createError) throw createError;

      // Get all messages from the original session
      const { data: messages, error: messagesError } = await supabase
        .from('product_chat_messages')
        .select('role, content')
        .eq('session_id', sessionId)
        .order('created_at', { ascending: true });

      if (messagesError) throw messagesError;

      // Copy messages to the new session, preserving conversation flow
      if (messages && messages.length > 0) {
        const newMessages = messages.map(msg => ({
          session_id: newSession.id,
          role: msg.role,
          content: msg.content,
          user_id: user.id
        }));

        const { error: insertError } = await supabase
          .from('product_chat_messages')
          .insert(newMessages);

        if (insertError) throw insertError;
      }

      onDuplicate(newSession.id);
      toast.success('Chat duplicated successfully');
    } catch (error) {
      console.error('Error duplicating chat:', error);
      toast.error('Failed to duplicate chat');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!user) return;
    
    setIsLoading(true);
    try {
      // Delete messages first (cascade should handle this, but being explicit)
      await supabase
        .from('product_chat_messages')
        .delete()
        .eq('session_id', sessionId);

      // Delete the session
      const { error } = await supabase
        .from('product_chat_sessions')
        .delete()
        .eq('id', sessionId)
        .eq('user_id', user.id);

      if (error) throw error;

      onDelete(sessionId);
      toast.success('Chat deleted successfully');
    } catch (error) {
      console.error('Error deleting chat:', error);
      toast.error('Failed to delete chat');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className="h-8 w-8 p-0 hover:bg-accent"
          >
            <MoreHorizontal className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-40">
          <DropdownMenuItem onClick={() => setIsRenameOpen(true)} disabled={isLoading}>
            <Pencil className="h-4 w-4 mr-2" />
            Rename
          </DropdownMenuItem>
          <DropdownMenuItem onClick={handleDuplicate} disabled={isLoading}>
            <Copy className="h-4 w-4 mr-2" />
            Duplicate
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem 
            onClick={handleDelete} 
            disabled={isLoading}
            className="text-red-600 hover:text-red-700 focus:text-red-700"
          >
            <Trash2 className="h-4 w-4 mr-2" />
            Delete
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={isRenameOpen} onOpenChange={setIsRenameOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rename Chat</DialogTitle>
            <DialogDescription>
              Enter a new name for this chat conversation.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <Input
              value={newTitle}
              onChange={(e) => setNewTitle(e.target.value)}
              placeholder="Chat title..."
              maxLength={100}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && newTitle.trim()) {
                  handleRename();
                }
              }}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsRenameOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleRename} disabled={isLoading || !newTitle.trim()}>
              {isLoading ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ChatActions;
